import React, {useEffect, useRef, useState} from 'react';
import TextInput from '../../components/inputs/TextInput';
import CustomSelect from '../../components/inputs/CustomSelect';
import PhoneNumberInput from '../../components/inputs/PhoneNumberInput';
import SSNInput from '../../components/inputs/SSNInput';
import DateInput from '../../components/inputs/DateInput';
import {FaUser} from 'react-icons/fa';
import { useSelector} from 'react-redux';
import { RootState} from '../../store/store';
import {showToast} from '../../components/ToastContainer';
import {Location} from '../../types/location';
import {LocationService} from '../../services/locationService';
import {ReferralSource} from '../../types/referralSource';
import {ReferralSourceService} from '../../services/referralSourceService';
import WhiteButton from '../../components/WhiteButton';
import BlueButton from '../../components/BlueButton';
import FormSkeleton from '../../components/FormSkeleton';

interface ClientIntakeFormProps {
  closeModal: () => void;
  clientData: any;
  setClientData: (data: any) => void;
  onNext: (data: any) => void;
  onSave?: (data: any) => void;
  isIntakeValid: boolean;
  pictureDeleted?: boolean;
  setPictureDeleted?: (deleted: boolean) => void;
}

const ClientIntakeForm: React.FC<ClientIntakeFormProps> = ({
  closeModal,
  clientData,
  setClientData,
  onNext,
  onSave,
  isIntakeValid,
  pictureDeleted,
  setPictureDeleted,
}) => {
  const client = useSelector((state: RootState) => state.client.client);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [locationOptions, setLocationsOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [referralSourceOptions, setReferralSourceOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const sexOptions = [
    {value: 'female', label: 'Female'},
    {value: 'male', label: 'Male'},
    {value: 'unKnown', label: 'UnKnown'},
  ];

  const ethnicityOptions = [
    {value: 'none', label: 'None Selected'},
    {value: 'hispanic_latino', label: 'Hispanic/Latino'},
    {
      value: 'american_indian_alaskan_native',
      label: 'American Indian or Alaskan Native',
    },
    {value: 'asian', label: 'Asian'},
    {value: 'black_african_american', label: 'Black/African American'},
    {value: 'hawaiian_pacific_islander', label: 'Hawaiian/Pacific Islander'},
    {value: 'biracial', label: 'Biracial'},
    {value: 'white_caucasian', label: 'White/Caucasian'},
    {value: 'middle_eastern', label: 'Middle Eastern'},
  ];

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const [locationsResponse, referralSourcesResponse] = await Promise.all([
          LocationService.index(1, {where: {}}, '', '', 100),
          ReferralSourceService.index(1, {where: {}}, '', '', 100),
        ]);
        
        const locationOpts =
          locationsResponse.data?.map((location: Location) => ({
            label: location.name,
            value: location.id,
          })) || [];
        setLocationsOptions(locationOpts);

        const referralSourceOpts =
          referralSourcesResponse.data?.map((referralSource: ReferralSource) => ({
            label: `${referralSource.name} - ${referralSource.agency}`,
            value: referralSource.id,
          })) || [];
        setReferralSourceOptions(referralSourceOpts);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleCancel = () => {
    setClientData({...client});
    closeModal();
  };

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    if (!id) return;
    setClientData((prev: any) => ({...prev, [id]: value}));
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setClientData((prev: any) => ({ ...prev, picture: file }));
      showToast('Profile picture selected.', 'success');
    }
  };

  const handleDelete = () => {
    if (client?.pictureUrl && !pictureDeleted) {
      // Mark existing picture for deletion
      setPictureDeleted?.(true);
      showToast('Profile picture marked for deletion. Save to confirm.', 'success');
    } else {
      // Remove new picture or reset deletion
      setClientData((prev: any) => ({ ...prev, picture: null }));
      setPictureDeleted?.(false);
      showToast('Profile picture removed.', 'success');
    }
  };

  return (
    <div className=" px-4 w-full">
      {isLoading ? (
        <FormSkeleton rows={5} />
      ) : (
      <form
        onSubmit={e => {
          e.preventDefault();
        }}
        className="flex flex-col  items-center  w-full">
        <input
          type="file"
          ref={fileInputRef}
          style={{display: 'none'}}
          accept="image/*"
          onChange={handleFileChange}
        />
        <div className="flex items-center gap-4 mb-6 py-2 w-full justify-center">
          <div className="flex-shrink-0 w-32 h-32 mt-1 bg-slate-100 rounded-full flex justify-center items-center overflow-hidden">
            {client?.pictureUrl && !pictureDeleted ? (
              <img
                src={client.pictureUrl}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : clientData.picture && clientData.picture instanceof File ? (
              <img
                src={URL.createObjectURL(clientData.picture)}
                alt="Profile Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <FaUser className="text-gray-400 size-8" />
            )}
          </div>
          <button
            type="button"
            disabled={avatarLoading}
            className="py-2.5 px-3 w-auto inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-cyan-400 text-white hover:bg-cyan-500 disabled:opacity-50 disabled:pointer-events-none dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600"
            onClick={() => fileInputRef.current?.click()}>
            Upload New Picture
          </button>
          <button
            type="button"
            className={`py-2.5 px-3 w-auto inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg text-slate-500 bg-white disabled:pointer-events-none dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600 ${!((client?.pictureUrl && !pictureDeleted) || (clientData.picture && clientData.picture instanceof File)) ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleDelete}
            disabled={!((client?.pictureUrl && !pictureDeleted) || (clientData.picture && clientData.picture instanceof File)) || avatarLoading}>
            Delete
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
          <TextInput
            id="firstName"
            type="text"
            label="First Name"
            showAsterisk={true}
            value={clientData.firstName || ''}
            onChange={handleChange}
          />
          <TextInput
            id="lastName"
            type="text"
            label="Last Name"
            showAsterisk={true}
            value={clientData.lastName || ''}
            onChange={handleChange}
          />
          <TextInput
            id="aliasName"
            type="text"
            label="Alias Name"
            value={clientData.aliasName || ''}
            onChange={handleChange}
          />
          <SSNInput
            id="ssn"
            label="SSN"
            value={clientData.ssn || ''}
            onChange={handleChange}
          />
          <TextInput
            id="legalGuardian"
            type="text"
            label="Legal Guardian"
            value={clientData.legalGuardian || ''}
            onChange={handleChange}
          />
          <TextInput
            id="externalId"
            type="text"
            label="External ID"
            value={clientData.externalId || ''}
            onChange={handleChange}
          />
          <div className="flex flex-col gap-[0.625rem]">
            <label
              htmlFor="sex"
              className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              Sex
              <span className="text-slate-800">*</span>
            </label>
            <CustomSelect
              options={sexOptions}
              isMulti={false}
              onChange={selectedOption => {
                setClientData({
                  ...clientData,
                  sex: selectedOption ? selectedOption.value : '',
                });
              }}
              value={clientData.sex}
            />
          </div>
          <div className="flex flex-col gap-[0.625rem]">
            <label
              htmlFor="locationIds"
              className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              Location
              <span className="text-slate-800">*</span>
            </label>
            <CustomSelect
              options={locationOptions}
              isMulti={false}
              onChange={selectedOption => {
                setClientData({
                  ...clientData,
                  locationId: selectedOption ? selectedOption.value : '',
                });
              }}
              value={clientData.locationId}
            />
          </div>
          <div className="flex flex-col gap-[0.625rem]">
            <label
              htmlFor="ethnicity"
              className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              Ethnicity
            </label>
            <CustomSelect
              options={ethnicityOptions}
              isMulti={false}
              menuPlacement={'top'}
              onChange={selectedOption => {
                setClientData({
                  ...clientData,
                  ethnicity: selectedOption ? selectedOption.value : '',
                });
              }}
              value={clientData.ethnicity}
            />
          </div>
          <DateInput
            id="dateOfBirth"
            type="date"
            value={clientData.dateOfBirth || ''}
            onChange={handleChange}
            placeholder="Date of Birth"
            allowPastDates={true}
            label="Date of Birth"
            calendarDirection="up"
          />
          <TextInput
            id="email"
            type="text"
            label="Email"
            value={clientData.email || ''}
            onChange={handleChange}
          />
          <PhoneNumberInput
            id="phoneNumber"
            label="Phone Number"
            value={clientData.phoneNumber || ''}
            onChange={handleChange}
            phoneNumber={clientData.phoneNumber || ''}
            setPhoneNumber={(phoneNumber: string) =>
              setClientData((prev: any) => ({...prev, phoneNumber}))
            }
          />
        </div>
        <div className="w-full mt-6">
          <label
            htmlFor="referralSource"
            className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white mb-2">
            Referal Source
          </label>
          <CustomSelect
            options={referralSourceOptions}
            isMulti={false}
            menuPlacement={'top'}
            onChange={selectedOption => {
              setClientData({
                ...clientData,
                referralSourceId: selectedOption ? selectedOption.value : '',
              });
            }}
            value={clientData.referralSourceId}
          />
        </div>
      </form>
      )}
      <footer className="flex-shrink-0 pb-4 pt-6 flex justify-end gap-2  w-full">
        <WhiteButton onClick={handleCancel} label="Cancel" />
        <BlueButton
          onClick={() => {
            if (client?.id && onSave) {
              onSave(clientData);
            } else {
              onNext(clientData);
            }
          }}
          label={client?.id ? "Save" : "Next"}
          inputId="modal-blue-button"
          disabled={!isIntakeValid}
        />
      </footer>
    </div>
  );
};
export default ClientIntakeForm;
